import "antd/dist/antd.variable.less";
import "@/styles/root.less";
import "@/styles/base.less";
import "@/styles/globals.less";
import "@/styles/base2.less";
import "@/styles/globals2.less";
import "@/styles/plugin.less";
import React from "react";
import { useRouter } from "next/router";
import Script from "next/script";
import { ConfigProvider } from "antd";
import useWindowResize from "hooks/useWindowResize";
import { Context, XtLocale, Cookie, Request, Seo, Hooks, Platform, Bridge, Util, isRtlLocale, resolveSeoPagePath } from "@xt/xtbase";
const { XtContext, XtProvider } = Context;
const { setHeader } = Request;
import { message, Modal } from "antd";
import "animate.css";
import "hint.css";
import models, { StoreProvider, useStore } from "@/models";
import { NavStoreProvider } from "@xt/Nav";
import langMessages from "locales";
import tradeStore from "store";
import Head from "next/head";
import '@/utils/axios';
import XtMessage from "../components/App/message";
import PWAInstallPrompt from "../components/App/PWAInstallPrompt";
import PWAUpdatePrompt from "../components/App/PWAUpdatePrompt";
// import CustomerService from "../components/App/CustomerService";
import { useServiceWorkerRegistration } from "@/hooks/useServiceWorkerRegistration";
import { localesConfig } from "../utils/display";
import { getZendeskLocale } from "@/utils/method";
import { trackFbPixelPageView, getFbPixelId } from "@/utils/facebookPixel";
import { getCountryCodeByIP } from "@/utils/fiatMarket";
import { initBehaviorTracking, syncBehaviorAuth } from "@/utils/behaviorTracker";
import { trackRouteChange } from "@/utils/clientBehavior";
/** 站点图标 / manifest / favicon 缓存版本：换图或换路径后递增，需与 public/manifest.json 中 ?v= 保持一致 */
const APP_ICON_VERSION = "6";
const faviconIco = `/favicon.ico?v=${APP_ICON_VERSION}`;
const appIcon180 = `/icons/icon-180x180.png?v=${APP_ICON_VERSION}`;
const appIcon192 = `/icons/icon-192x192.png?v=${APP_ICON_VERSION}`;
const appIcon512 = `/icons/icon-512x512.png?v=${APP_ICON_VERSION}`;
// Firebase / moment 使用动态导入，避免 _app 主 chunk 过大（原 7MB+）

const specialLangPagePathname = '/affiliate/console';
const MANUAL_LANG_COOKIE = "langManual";
const ARABIC_COUNTRY_CODES = ["AE", "SA"];

import Package from '/package.json'
message.config({
  maxCount: 1,
});
const { useTranslation } = Hooks;
const { getUUID } = Util;
const ContextToStore = ({ children }) => {
  const [appState, appDispatch] = React.useContext(XtContext);
  const store = useStore();
  // 强制使用 dark 作为默认主题，忽略 Cookie 中的值
  console.log("children.props.theme", children.props);
  const theme = children.props.theme || "dark";
  const currency = Cookie.get("currency");
  
  // 立即设置 body 的 data-theme 属性
  if (typeof window !== "undefined") {
    const htmlDom = document.querySelector('body');
    htmlDom?.setAttribute('data-theme', theme);
  }
  
  // 注册Service Worker
  useServiceWorkerRegistration();
  
  React.useEffect(() => {
    appDispatch({
      payload: {
        theme,
        currency,
      },
    });
    store.init(appState);
  }, []);
  React.useEffect(() => {
    store.fund.setAssist(appState.currency);
  }, [appState.currency]);
  React.useEffect(() => {
    store.user.setLoginStatus(appState.loginStatus);
  }, [appState.loginStatus]);
  React.useEffect(() => {
    tradeStore.user.bindMainState(appState);
    syncBehaviorAuth({
      loginStatus: appState.loginStatus,
      userInfo: appState.userInfo,
    });
  }, [appState]);

  // PWA standalone 时给 html 加类，仅此时底部导航使用“安全区上下均分”居中，不影响浏览器内样式
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches
      || !!(window.navigator && window.navigator.standalone);
    if (isStandalone) document.documentElement.classList.add("pwa-standalone");
  }, []);

  // vConsole：仅 QA 构建（.env.qa 含 NEXT_PUBLIC_ENABLE_VCONSOLE），test 构建（.env.test）不启用
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const on =
      process.env.NEXT_PUBLIC_ENABLE_VCONSOLE === "1" ||
      process.env.NEXT_PUBLIC_ENABLE_VCONSOLE === "true";
    if (!on) return;
    import("vconsole").then((module) => {
      const VConsole = module.default;
      if (VConsole && !window.__vConsole) {
        window.__vConsole = new VConsole();
      }
    });
  }, []);

  // AWS CloudWatch RUM（pro / qa 环境上报）
  React.useEffect(() => {
    if (typeof window === "undefined" || !["pro", "qa"].includes(process.env.NEXT_PUBLIC_ENV)) return;
    import("@/utils/awsRum").then((m) => m.initAwsRum());
  }, []);

  return children;
};

// 按当前语言动态加载 moment 语言包，避免主 chunk 打包全部 locale
const MOMENT_LOCALE_MAP = {
  "zh-CN": "zh-cn",
  "zh-HK": "zh-tw",
  "ja": "ja",
  "ko": "ko-kr",
  "tr": "tr",
  "th": "th",
  "ru": "ru",
  "es": "es",
  "vi": "vi",
  "id": "id",
  "hi": "hi",
};
function useMomentLocale(locale) {
  React.useEffect(() => {
    const momentKey = MOMENT_LOCALE_MAP[locale] || "en";
    if (momentKey === "en") return; // moment 默认 en
    import(`moment/locale/${momentKey}`).catch(() => {});
  }, [locale]);
}

const localeToLangKey = (value) => {
  if (!value || value === "default") return undefined;
  if (value === "zh-CN") return "cn";
  if (value === "zh-HK") return "hk";
  return value;
};

const langKeyToLocale = (value, localeList = []) => {
  if (!value) return undefined;
  const nextLocale = value === "cn" ? "zh-CN" : value === "hk" ? "zh-HK" : value;
  if (localeList?.length && !localeList.includes(nextLocale)) return undefined;
  return nextLocale;
};

/**
 * Vue 托管路径是否跳过 URL 语言切换，须以地址栏为准。
 * /wallet 等无 Next pages 项时 router.pathname 常为 /_error、/404，router.asPath 也可能短暂为空或与地址栏不一致。
 */
function pathForVueSpaLocaleSkip(routerAsPathFallback) {
  if (typeof window === "undefined") return routerAsPathFallback || "";
  const p = window.location.pathname || "";
  const q = window.location.search || "";
  return p + q || routerAsPathFallback || "";
}

/** 当前地址是否为 web-index Vue 托管路径：此类路径禁止 Next 在 URL 上切换 locale */
function shouldSkipLocaleUrlForVueSpa(router) {
  return Util.webIndexSpaPathNoIntlLocale(pathForVueSpaLocaleSkip(router.asPath));
}

/**
 * 按 IP / Cookie 自动选语言时改写 URL（仅非 Vue 托管路径）。
 * pathname 为 /_error、/404 等与地址栏不一致时，用 asPath 作为 replace 第一参，避免错误导航。
 */
function replaceLocaleInUrl(router, nextLocale) {
  if (!nextLocale || nextLocale === router.locale) return;
  if (shouldSkipLocaleUrlForVueSpa(router)) return;
  const { pathname, asPath } = router;
  const useAsPathOnly = pathname === "/_error" || pathname === "/404" || pathname === "/500";
  if (useAsPathOnly) {
    router.replace(asPath, asPath, { locale: nextLocale });
  } else {
    router.replace(pathname, asPath, { locale: nextLocale });
  }
}

function MyApp({ Component, pageProps }) {
  const router = useRouter();
  const { locale, pathname, locales, defaultLocale } = router;
  const isRtl = isRtlLocale(locale);
  useMomentLocale(locale);
  const initLocales = pathname?.includes(specialLangPagePathname) ? locales : localesConfig;
  XtLocale.init(locale, initLocales, langMessages);
  const { t } = useTranslation();
  const te = (id) => {
    if (!id) return "";
    const str = getUUID();
    const message = XtLocale.intl?.formatMessage({ id, defaultMessage: str });
    if (message === str || message == null) return false;
    return true;
  };

  const localeObj = XtLocale.getLocaleObj(locale);
  console.log("app useRouter", { locale, locales, defaultLocale }, localeObj.key);
  const payload = {
    lang: localeObj.key,
    theme: "dark", // 默认使用 dark 主题
  };
  React.useEffect(() => {
    if (typeof window === "undefined" || !router.isReady) return;
    // App WebView 内语言由 App 控制（URL locale），跳过 IP 检测
    if (Platform.isInApp()) return;
    // Vue web-index 路径下禁止 router.replace(..., { locale })，否则 Next 会把地址改成 /en/wallet/... 并 404；仍可写 Cookie
    const cookieLang = Cookie.get("lang");
    const hasManualLang = Cookie.get(MANUAL_LANG_COOKIE) === "1";
    const preferredLocale = hasManualLang ? langKeyToLocale(cookieLang, locales) : undefined;
    if (preferredLocale) {
      if (preferredLocale !== locale) {
        replaceLocaleInUrl(router, preferredLocale);
      }
      return;
    }
    let cancelled = false;
    getCountryCodeByIP().then((countryCode) => {
      if (cancelled || !countryCode) return;
      const targetLocale = countryCode === "BR" ? "pt" : countryCode === "VN" ? "vi" : ARABIC_COUNTRY_CODES.includes(countryCode) ? "ar" : "en";
      const targetLangKey = localeToLangKey(targetLocale) || targetLocale;
      Cookie.set("lang", targetLangKey);
      // 用 router.locale：异步返回时须与当前路由语言比较，避免闭包里的 locale 已过期
      if (targetLocale !== router.locale) {
        replaceLocaleInUrl(router, targetLocale);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [locale, locales, router.asPath, router.isReady, pathname]);
  React.useEffect(() => {
    const dir = isRtl ? "rtl" : "ltr";
    document.documentElement.setAttribute("dir", dir);
    document.documentElement.setAttribute("data-dir", dir);
  }, [isRtl]);

  React.useEffect(() => {
    tradeStore.app.updateState({ clientSideReady: true });
    const keyCodeMap = {
      // 91: true, // command
      61: true,
      107: true, // 数字键盘 +
      109: true, // 数字键盘 -
      173: true, // 火狐 - 号
      187: true, // +
      189: true, // -
    };
    // 覆盖ctrl||command + ‘+’/‘-’
    document.onkeydown = function (event) {
      const e = event || window.event;
      const ctrlKey = e.ctrlKey || e.metaKey;
      if (ctrlKey && keyCodeMap[e.keyCode]) {
        e.preventDefault();
      } else if (e.detail) {
        // Firefox
        event.returnValue = false;
      }
    };
    // 覆盖鼠标滑动
    document.body.addEventListener(
      "wheel",
      (e) => {
        if (e.ctrlKey) {
          if (e.deltaY < 0) {
            e.preventDefault();
            return false;
          }
          if (e.deltaY > 0) {
            e.preventDefault();
            return false;
          }
        }
      },
      { passive: false }
    );

    let clientCode = Cookie.get("clientCode");
    if (!clientCode || clientCode.length !== 32) {
      clientCode = Date.now() + generateNonceStr(19);
      Cookie.set("clientCode", clientCode);
    }
    setHeader({ "client-code": clientCode });
    initBehaviorTracking();
    const token = (Platform.isInApp() ? Bridge.getToken() : null) || Cookie.get("token");
    if (token) {
      setHeader({'Authorization': `Bearer ${token}`});
      token && models?.account?.setIsLogin(true);
    }
    models?.consl.setLang(locale);

    message.config({
      top: 60,
    });
    ConfigProvider.config({
      theme: {
        primaryColor: "#B9F78A",
      },
    });
    const htmlDom = document.querySelector("html");
    htmlDom && htmlDom.setAttribute("translate", "no");
    // 客服已替换为 LiveHelp100，以下 Zendesk 加载注释保留，后续可能恢复
    // if (!Platform.isInApp() && !pathname.includes("red-packet")) {
    //   document.body.classList.add("zendesk-hide");
    //   (function () {
    //     var script = document.createElement("script");
    //     script.setAttribute("src", "https://static.zdassets.com/ekr/snippet.js?key=8d996cc2-d7cd-4cab-a32d-d714c739f6ce");
    //     script.setAttribute("async", "true");
    //     script.setAttribute("id", "ze-snippet");
    //     script.setAttribute("crossorigin", "anonymous");
    //     script.onload = function () {
    //       var zE = (typeof window !== "undefined" && (window.zE || window.$zE));
    //       if (typeof zE !== "function") return;
    //       var zenLocale = getZendeskLocale(XtLocale.getZenLan());
    //       try {
    //         zE("webWidget", "setLocale", zenLocale);
    //         zE("messenger:set", "locale", zenLocale);
    //         zE("webWidget", "hide");
    //         zE("messenger", "hide");
    //         zE("webWidget:on", "close", function () { try { zE("webWidget", "hide"); } catch (e) {} });
    //         var doMessengerHide = function () { try { zE("messenger", "hide"); } catch (e) {} };
    //         zE("messenger:on", "close", function () {
    //           doMessengerHide();
    //           [50, 200, 500, 1000, 2000].forEach(function (ms) { setTimeout(doMessengerHide, ms); });
    //         });
    //       } catch (e) {}
    //     };
    //     document.body.appendChild(script);
    //   })();
    // }
    // Firebase 动态加载（GA 埋点 + Performance 上报），pro / qa 上报
    import("@/utils/firebase").then((m) => m.shouldInitFirebase() && m.initFirebase()).catch(() => {});
  }, []);

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const ROUTER_DEBUG_LOG_KEY = "__routerDebugLogs__";

    const readLogs = () => {
      try {
        const savedLogs = window.sessionStorage.getItem(ROUTER_DEBUG_LOG_KEY);
        return savedLogs ? JSON.parse(savedLogs) : [];
      } catch (error) {
        return [];
      }
    };

    const safeClone = (value) => {
      try {
        return JSON.parse(
          JSON.stringify(value, (key, currentValue) => {
            if (currentValue instanceof Error) {
              return {
                name: currentValue.name,
                message: currentValue.message,
                stack: currentValue.stack,
              };
            }
            if (typeof currentValue === "function") {
              return `[Function ${currentValue.name || "anonymous"}]`;
            }
            if (typeof currentValue === "undefined") {
              return "[undefined]";
            }
            return currentValue;
          })
        );
      } catch (error) {
        return String(value);
      }
    };

    const persistLogs = (logs) => {
      window.__routerDebugLogs = logs;
      try {
        window.sessionStorage.setItem(ROUTER_DEBUG_LOG_KEY, JSON.stringify(logs));
      } catch (error) {}
    };

    const toDebugString = (value) => {
      try {
        const normalizedValue = safeClone(value);
        return typeof normalizedValue === "string" ? normalizedValue : JSON.stringify(normalizedValue);
      } catch (error) {
        return String(value);
      }
    };

    const getErrorSummary = (error) => {
      if (!error) return "";

      const parts = [];
      if (error.name) parts.push(`name=${error.name}`);
      if (error.message) parts.push(`message=${error.message}`);
      if (typeof error.cancelled !== "undefined") parts.push(`cancelled=${String(error.cancelled)}`);
      if (error.code) parts.push(`code=${error.code}`);
      if (error.type) parts.push(`type=${error.type}`);
      if (error.request) parts.push(`request=${error.request}`);
      if (error.digest) parts.push(`digest=${error.digest}`);
      if (error.srcElement?.src) parts.push(`src=${error.srcElement.src}`);
      if (error.target?.src) parts.push(`targetSrc=${error.target.src}`);
      if (error.stack) parts.push(`stack=${String(error.stack).replace(/\s+/g, " ").trim()}`);

      return parts.join(" | ");
    };

    const printLog = (text) => {
      try {
        const consoleRef = globalThis["console"];
        const logger = consoleRef && (consoleRef.info || consoleRef.log);
        if (typeof logger === "function") {
          logger.call(consoleRef, text);
        }
      } catch (error) {}
    };

    const log = (type, detail = null) => {
      const logItem = {
        type,
        detail: safeClone(detail),
        time: new Date().toISOString(),
        href: window.location.href,
        asPath: router.asPath,
        pathname: router.pathname,
        locale: router.locale,
      };
      logItem.detailText = toDebugString(logItem.detail);
      const nextLogs = [...readLogs(), logItem].slice(-200);

      persistLogs(nextLogs);
      printLog(`[ROUTER_DEBUG] ${type} ${toDebugString(logItem)}`);
    };

    window.__routerDebugEnabled = true;
    window.__routerDebugLog = log;
    window.__getRouterDebugLogs = readLogs;

    const navigationEntry = window.performance?.getEntriesByType?.("navigation")?.[0];
    log("enabled", {
      navigationType: navigationEntry?.type,
      referrer: document.referrer,
    });

    const originalPush = router.push.bind(router);
    const originalReplace = router.replace.bind(router);
    const originalHistoryPushState = window.history.pushState.bind(window.history);
    const originalHistoryReplaceState = window.history.replaceState.bind(window.history);

    router.push = async (...args) => {
      log("router.push called", { args });
      try {
        const result = await originalPush(...args);
        log("router.push resolved", { result });
        return result;
      } catch (error) {
        log("router.push failed", { error });
        throw error;
      }
    };

    router.replace = async (...args) => {
      log("router.replace called", { args });
      try {
        const result = await originalReplace(...args);
        log("router.replace resolved", { result });
        return result;
      } catch (error) {
        log("router.replace failed", { error });
        throw error;
      }
    };

    window.history.pushState = function (...args) {
      log("history.pushState", { args });
      return originalHistoryPushState(...args);
    };

    window.history.replaceState = function (...args) {
      log("history.replaceState", { args });
      return originalHistoryReplaceState(...args);
    };

    const handleRouteChangeStart = (url, options) => log("routeChangeStart", { url, options });
    const handleBeforeHistoryChange = (url, options) => log("beforeHistoryChange", { url, options });
    const handleRouteChangeComplete = (url, options) => log("routeChangeComplete", { url, options });
    const handleRouteChangeError = (error, url, options) =>
      log("routeChangeError", {
        url,
        options,
        errorName: error?.name || "",
        errorMessage: error?.message || "",
        errorCancelled: typeof error?.cancelled === "undefined" ? "" : String(error.cancelled),
        errorCode: error?.code || "",
        errorType: error?.type || "",
        errorRequest: error?.request || "",
        errorTargetSrc: error?.target?.src || error?.srcElement?.src || "",
        errorStack: error?.stack || "",
        errorSummary: getErrorSummary(error),
      });
    const handleWindowError = (event) =>
      log("window.error", {
        message: event?.message || "",
        filename: event?.filename || "",
        lineno: event?.lineno || "",
        colno: event?.colno || "",
        errorSummary: getErrorSummary(event?.error),
      });
    const handleUnhandledRejection = (event) =>
      log("unhandledrejection", {
        reasonText: toDebugString(event?.reason),
        reasonSummary: getErrorSummary(event?.reason),
      });
    const handleHashChangeStart = (url, options) => log("hashChangeStart", { url, options });
    const handleHashChangeComplete = (url, options) => log("hashChangeComplete", { url, options });
    const handleBeforeUnload = (event) => log("beforeunload", { href: window.location.href, type: event.type });
    const handlePageHide = (event) => log("pagehide", { href: window.location.href, persisted: event.persisted });
    const handlePopState = (event) => log("popstate", { href: window.location.href, state: event.state });

    router.events.on("routeChangeStart", handleRouteChangeStart);
    router.events.on("beforeHistoryChange", handleBeforeHistoryChange);
    router.events.on("routeChangeComplete", handleRouteChangeComplete);
    router.events.on("routeChangeError", handleRouteChangeError);
    router.events.on("hashChangeStart", handleHashChangeStart);
    router.events.on("hashChangeComplete", handleHashChangeComplete);
    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("pagehide", handlePageHide);
    window.addEventListener("popstate", handlePopState);
    window.addEventListener("error", handleWindowError);
    window.addEventListener("unhandledrejection", handleUnhandledRejection);

    return () => {
      router.push = originalPush;
      router.replace = originalReplace;
      window.history.pushState = originalHistoryPushState;
      window.history.replaceState = originalHistoryReplaceState;
      delete window.__routerDebugEnabled;
      delete window.__routerDebugLog;
      delete window.__getRouterDebugLogs;
      router.events.off("routeChangeStart", handleRouteChangeStart);
      router.events.off("beforeHistoryChange", handleBeforeHistoryChange);
      router.events.off("routeChangeComplete", handleRouteChangeComplete);
      router.events.off("routeChangeError", handleRouteChangeError);
      router.events.off("hashChangeStart", handleHashChangeStart);
      router.events.off("hashChangeComplete", handleHashChangeComplete);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("pagehide", handlePageHide);
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("error", handleWindowError);
      window.removeEventListener("unhandledrejection", handleUnhandledRejection);
    };
  }, [router]);

  // 语言切换时同步更新 Zendesk widget 语言（已注释，后续可能恢复）
  // React.useEffect(() => {
  //   if (typeof window === "undefined") return;
  //   var zE = window.zE || window.$zE;
  //   if (typeof zE !== "function") return;
  //   var zenLocale = getZendeskLocale(XtLocale.getZenLan());
  //   try {
  //     zE("webWidget", "setLocale", zenLocale);
  //     zE("messenger:set", "locale", zenLocale);
  //   } catch (e) {}
  // }, [locale]);

  // Facebook Pixel：路由变化时补发 PageView（首屏由 base snippet 发送）
  React.useEffect(() => {
    if (typeof window === "undefined" || !getFbPixelId()) return;
    trackFbPixelPageView();
  }, [pathname]);

  const behaviorPrevPathRef = React.useRef(null);
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const nextPath = router.asPath || pathname;
    if (behaviorPrevPathRef.current !== nextPath) {
      trackRouteChange(nextPath, behaviorPrevPathRef.current);
      behaviorPrevPathRef.current = nextPath;
    }
  }, [pathname, router.asPath]);

  useWindowResize(() => {
    let breakpoint;
    let tradeBreakpoint;
    const { innerWidth } = window;
    if (innerWidth >= 1698) {
      breakpoint = "xl";
      tradeBreakpoint = "xl";
    } else if (innerWidth > 1200) {
      breakpoint = "lg";
      tradeBreakpoint = "lg";
    } else if (innerWidth > 992) {
      breakpoint = "md";
      tradeBreakpoint = "md";
    } else if (innerWidth > 768) {
      breakpoint = "sm";
      tradeBreakpoint = "md";
    } else {
      breakpoint = "xs";
      tradeBreakpoint = "sm";
    }
    models?.app?.updateState({ breakpoint });
    tradeStore.app.updateState({ breakpoint: tradeBreakpoint, windowInnerWidth: innerWidth });
    document.body.setAttribute("data-breakpoint", breakpoint);
  });

  const domain = process.env.NEXT_PUBLIC_DEV ? "" : process.env.NEXT_PUBLIC_cdn + "/public";
  // 与 Layout / CFD / More 主背景一致，避免 Safari 顶栏 theme-color 与页面内容色差
  const themeColor = "#181a20";
  const seoPagePath = resolveSeoPagePath(pageProps, router);

  return (
    <XtProvider payload={payload} intlValue={XtLocale.intl}>
      <ConfigProvider locale={localeObj.antd} direction={isRtl ? "rtl" : "ltr"} autoInsertSpaceInButton={false}>
        <Seo tdk={pageProps.tdk} lang={locale} resolvedUrl={seoPagePath} Head={Head}/>
        <Head>
          <link rel="manifest" href={`/manifest.json?v=${APP_ICON_VERSION}`} />
          <link rel="icon" href={faviconIco} sizes="any" />
          <link rel="icon" type="image/png" sizes="192x192" href={appIcon192} />
          <link rel="icon" type="image/png" sizes="512x512" href={appIcon512} />
          <meta name="theme-color" content={themeColor} />
          <meta name="apple-mobile-web-app-capable" content="yes" />
          <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
          <meta name="apple-mobile-web-app-title" content="Spikex" />
          <link rel="apple-touch-icon" href={appIcon180} sizes="180x180" />
          <meta name="mobile-web-app-capable" content="yes" />
          <meta name="msapplication-TileColor" content={themeColor} />
          <meta name="msapplication-TileImage" content={appIcon192} />
        </Head>
        {/* Meta Facebook Pixel：像素 ID / 测试事件码见 .env 或文档 */}
        {getFbPixelId() && (
          <>
            <Script
              id="facebook-pixel"
              strategy="afterInteractive"
              dangerouslySetInnerHTML={{
                __html: `
!function(f,b,e,v,n,t,s)
{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};
if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];
s.parentNode.insertBefore(t,s)}(window, document,'script',
'https://connect.facebook.net/en_US/fbevents.js');
fbq('init', '${getFbPixelId()}');
fbq('track', 'PageView'${process.env.NEXT_PUBLIC_FB_PIXEL_TEST_CODE ? `, {}, { test_event_code: '${process.env.NEXT_PUBLIC_FB_PIXEL_TEST_CODE}' }` : ""});
                `,
              }}
            />
            <noscript>
              <img
                height="1"
                width="1"
                style={{ display: "none" }}
                src={`https://www.facebook.com/tr?id=${getFbPixelId()}&ev=PageView&noscript=1`}
                alt=""
              />
            </noscript>
          </>
        )}
        {/* Firebase Performance：First Input Delay polyfill，pro / qa 加载 */}
        {["pro", "qa"].includes(process.env.NEXT_PUBLIC_ENV) && (
          <Script
            src="https://cdn.jsdelivr.net/npm/first-input-delay@0.1.3/dist/first-input-delay.min.js"
            strategy="beforeInteractive"
          />
        )}
        <Script dangerouslySetInnerHTML={{
          __html: `
          sessionStorage.clear();
          `
        }}>
        </Script>        <Script src={domain + "/iconpark.js?v=" + Package.version} />
        <link rel="stylesheet" type="text/css" href="https://at.alicdn.com/t/font_2502537_ytndssfkiy.css" />
        <StoreProvider value={models}>
          <NavStoreProvider store={models}>
          <XtMessage />
          <PWAInstallPrompt />
          <PWAUpdatePrompt />
          {/* <CustomerService show={!pathname.includes('red-packet')} /> */}
          <ContextToStore>
            <Component {...pageProps} />
          </ContextToStore>
          </NavStoreProvider>
        </StoreProvider>
      </ConfigProvider>
    </XtProvider>
  );
}

export default MyApp;

function generateNonceStr(length, type) {
  let chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  type === "num" && (chars = "0123456789");
  const maxPos = chars.length;
  let noceStr = "";
  for (let i = 0; i < (length || 32); i++) {
    noceStr += chars.charAt(Math.floor(Math.random() * maxPos));
  }
  return noceStr;
}
